import { createSelector } from '@ngrx/store';
import { Option } from '@sapphire/result';
import { mapSurveyLocalization } from '@ps/survey-utils';
import { surveyCreationFeature } from '../survey-creation.feature';
import { DEFAULT_CORE_CHILD_STATE } from '../survey-creation-state.constants';
import { resolveActiveCoreChildKey } from './utils/core-children.helper';
import { allowsAudienceRecalculation } from './utils/audience-editing-policy';

const { selectCore } = surveyCreationFeature;

export const selectRootSurveyId = createSelector(
  selectCore,
  (state) => state.rootSurveyId,
);

export const selectActiveChildKey = createSelector(
  selectCore,
  (state) => state.activeChildKey,
);

export const selectKind = createSelector(selectCore, (state) => state.kind);

export const selectCoreChildren = createSelector(
  selectCore,
  (state) => state.children,
);

// Only locales actually participating. participant is null (not false) for a standard survey
// or lone pre-draft locale — "no family" — and still counts as participating; only an explicit
// false (opted out) excludes it.
export const selectParticipantLocales = createSelector(
  selectCoreChildren,
  (children): ReadonlySet<string> =>
    new Set(
      Object.entries(children)
        .filter(([, child]) => child.participant !== false)
        .map(([key, child]) =>
          Option.from(child.survey).mapOr(key, (survey) =>
            mapSurveyLocalization(survey.countryCode, survey.languageShortCode),
          ),
        ),
    ),
);

const selectActiveCoreChild = createSelector(selectCore, (state) => {
  const key = resolveActiveCoreChildKey(state);
  return state.children[key] ?? DEFAULT_CORE_CHILD_STATE;
});

export const selectSurvey = createSelector(
  selectActiveCoreChild,
  ({ survey }) => survey,
);

export const selectSurveyId = createSelector(
  selectSurvey,
  (survey) => survey?.id ?? null,
);

// Derived from the survey itself rather than stored — see
// `audience-editing-policy`. Nothing has to keep a copy in sync.
export const selectSurveyAllowsRecalculation = createSelector(
  selectSurvey,
  allowsAudienceRecalculation,
);

export const selectIsSurveyLoaded = createSelector(
  selectSurvey,
  (survey) => !!survey,
);

export const selectError = createSelector(
  selectActiveCoreChild,
  ({ error }) => error,
);
